import { useState, useEffect, useRef, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Send, Loader2, Sparkles, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useOnboardingChat } from '@/hooks/useOnboardingChat';
import { IneaMark } from '@/components/IneaMark';
import { SectorPicker } from '@/components/SectorPicker';
import { useUserSectors } from '@/hooks/useSectors';
import { useAuth } from '@/hooks/useAuth';

/**
 * Onboarding — the conversational chat with Inea (Module 1).
 * Streaming bubbles, premium DA from network-ia.io.
 */
export default function OnboardingPage() {
  const { t } = useTranslation();
  const { messages, send, streaming, completed, error, loaded } = useOnboardingChat();
  const [draft, setDraft] = useState('');
  // État du bouton « Voir mon diagnostic » — déclaré ICI avec les autres hooks :
  // un hook placé après le `return` conditionnel du sélecteur de secteurs
  // changeait l'ordre des hooks entre deux rendus (crash React, vu le 18/09).
  const [opening, setOpening] = useState(false);
  const navigate = useNavigate();
  // Lot 1 — l'entretien est optionnel pour un membre déjà provisionné : il peut revenir.
  const { user, refresh } = useAuth();
  const scrollRef = useRef<HTMLDivElement>(null);

  // Sectors gate (Option D) — user must pick at least one activity before
  // the conversational chat starts. This makes Inea sector-aware from her
  // very first message instead of having to ask about it later.
  const { data: userSectors, isLoading: sectorsLoading } = useUserSectors();
  const [sectorStepDone, setSectorStepDone] = useState(false);
  const hasSectors = (userSectors?.sectors?.length ?? 0) > 0;
  // Mark step done if the user already has sectors (returning user) so we
  // don't gate the chat for them.
  useEffect(() => {
    if (hasSectors) setSectorStepDone(true);
  }, [hasSectors]);
  const showSectorStep = loaded && !sectorsLoading && !sectorStepDone;

  // Auto-scroll to the latest message.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  // Kick off the conversation automatically on first load — but ONLY after
  // the sector step is done (or already satisfied for returning users).
  useEffect(() => {
    if (loaded && sectorStepDone && messages.length === 0 && !streaming) {
      void send(t('onboarding.kickoff'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, sectorStepDone]);

  // Render the sector picker step BEFORE the chat starts.
  if (showSectorStep) {
    return <SectorPickerStep onContinue={() => setSectorStepDone(true)} />;
  }

  /**
   * « Voir mon diagnostic » — 17/09/2026.
   * Avant : navigate('/diagnostic') avec un cache ['auth','me'] figé à
   * onboarding_completed=false → RequireOnboarding renvoyait immédiatement sur
   * /onboarding. Boucle infinie : le diagnostic existait côté serveur mais
   * restait inatteignable. On relit donc la session avant de naviguer.
   */
  async function openDiagnostic(): Promise<void> {
    setOpening(true);
    try {
      await refresh();
    } catch {
      /* même si /auth/me échoue, on tente la navigation */
    } finally {
      setOpening(false);
      navigate('/diagnostic');
    }
  }

  function onSubmit(e: FormEvent): void {
    e.preventDefault();
    const text = draft.trim();
    if (!text || streaming || completed) return;
    setDraft('');
    void send(text);
  }

  return (
    <div className="mx-auto flex min-h-screen max-w-[760px] flex-col px-4 py-6 sm:px-6">
      {user?.onboarding_completed && !completed && (
        <button
          type="button"
          onClick={() => navigate('/')}
          className="mb-3 self-start text-[12.5px] font-600 text-ink/55 transition hover:text-ink"
        >
          ← {t('onboarding.backToDashboard')}
        </button>
      )}
      {/* Header — premium with glow */}
      <motion.header
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative mb-5"
      >
        <div
          className="pointer-events-none absolute -left-6 -top-8 h-32 w-32 rounded-full opacity-40 blur-3xl"
          style={{ background: 'radial-gradient(circle, #a855f7, transparent 70%)' }}
        />
        <div className="card relative flex items-center gap-3 p-3.5">
          <div className="animate-float">
            <IneaMark size={44} />
          </div>
          <div>
            <p className="font-mono text-[10px] font-700 uppercase tracking-[0.18em] text-ink/50">
              {t('onboarding.headerKicker')}
            </p>
            <h1 className="font-display text-lg font-700 tracking-[-0.02em]">Inea</h1>
            <p className="mt-0.5 text-[11.5px] leading-tight text-ink/55">
              {t('onboarding.optionalHint')}
            </p>
          </div>
          <span className="ml-auto flex items-center gap-1.5 rounded-full bg-green-500/10 px-3 py-1.5 text-[11.5px] font-600 text-green-700">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-60" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-green-500" />
            </span>
            {t('onboarding.online')}
          </span>
        </div>
      </motion.header>

      {/* Chat thread */}
      <div
        ref={scrollRef}
        className="card flex-1 space-y-4 overflow-y-auto p-5 sm:p-6"
        style={{ maxHeight: 'calc(100vh - 220px)' }}
      >
        <AnimatePresence initial={false}>
          {messages.map((m, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25 }}
              className={m.role === 'user' ? 'flex justify-end' : 'flex justify-start gap-2.5'}
            >
              {m.role === 'assistant' && <IneaMark size={30} />}
              <div
                className={
                  m.role === 'user'
                    ? 'max-w-[80%] rounded-2xl rounded-br-md px-4 py-2.5 text-[14px] leading-[1.55] text-white'
                    : 'max-w-[80%] rounded-2xl rounded-bl-md border border-ink/[0.07] bg-white px-4 py-2.5 text-[14px] leading-[1.55] text-ink'
                }
                style={
                  m.role === 'user'
                    ? { background: 'linear-gradient(135deg, #3b82f6, #ec4899)' }
                    : undefined
                }
              >
                {m.content || (
                  <span className="inline-flex gap-1">
                    <Dot delay={0} />
                    <Dot delay={0.15} />
                    <Dot delay={0.3} />
                  </span>
                )}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {error && (
        <p className="mt-3 rounded-xl bg-pink/10 px-3.5 py-2.5 text-[13px] font-500 text-pink-deep">
          {error}
        </p>
      )}

      {/* Completion banner OR input */}
      {completed ? (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="card-hi mt-4 flex items-center justify-between gap-4 rounded-2xl p-4"
        >
          <div className="flex items-center gap-2.5">
            <Sparkles size={18} className="text-pink" />
            <p className="text-[13.5px] font-600 text-ink">
              {t('onboarding.doneTitle')}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => { void openDiagnostic(); }}
              disabled={opening}
              className="btn-primary px-5 py-2.5 text-[13px] disabled:opacity-60"
            >
              {t('onboarding.doneCta')}
            </button>
            {/* Entretien recommencé (12/10/2026) : le membre était déjà onboardé en
                arrivant ; son identité attend un « Régénérer » sur Identité digitale. */}
            {user?.onboarding_completed && (
              <button onClick={() => navigate('/identite')} className="btn-ghost px-5 py-2.5 text-[13px]">
                {t('onboarding.updateIdentityCta')}
              </button>
            )}
          </div>
        </motion.div>
      ) : (
        <form onSubmit={onSubmit} className="mt-4 flex items-center gap-2.5">
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={t("onboarding.messagePlaceholder")}
            disabled={streaming}
            className="flex-1 rounded-2xl border border-ink/[0.12] bg-white px-4 py-3 text-[14px]
                       text-ink outline-none transition focus:border-pink/60 focus:ring-2
                       focus:ring-pink/20 disabled:opacity-60"
          />
          <button
            type="submit"
            disabled={streaming || !draft.trim()}
            className="btn-primary h-[46px] w-[46px] rounded-2xl !px-0 disabled:opacity-50"
            aria-label={t('onboarding.send')}
          >
            {streaming ? (
              <Loader2 size={17} className="animate-spin" />
            ) : (
              <Send size={16} strokeWidth={2.4} />
            )}
          </button>
        </form>
      )}
    </div>
  );
}

function Dot({ delay }: { delay: number }) {
  return (
    <motion.span
      className="inline-block h-1.5 w-1.5 rounded-full bg-ink/30"
      animate={{ opacity: [0.3, 1, 0.3] }}
      transition={{ duration: 1, repeat: Infinity, delay }}
    />
  );
}

/**
 * SectorPickerStep — Option D pre-chat sector picker.
 *
 * Affiché AVANT le chat conversationnel pour que Inea soit déjà adaptée
 * au métier de l'utilisateur dès son premier message. Animation
 * d'entrée douce + même DA que la page d'onboarding chat.
 */
function SectorPickerStep({ onContinue }: { onContinue: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="mx-auto flex min-h-screen max-w-[820px] flex-col px-4 py-6 sm:px-6">
      <motion.header
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative mb-5"
      >
        <div
          className="pointer-events-none absolute -left-6 -top-8 h-32 w-32 rounded-full opacity-40 blur-3xl"
          style={{ background: 'radial-gradient(circle, #a855f7, transparent 70%)' }}
        />
        <div className="card relative flex items-center gap-3 p-3.5">
          <div className="animate-float">
            <IneaMark size={44} />
          </div>
          <div>
            <p className="font-mono text-[10px] font-700 uppercase tracking-[0.18em] text-ink/50">
              {t('onboarding.sectorStepKicker')}
            </p>
            <h1 className="font-display text-lg font-700 tracking-[-0.02em]">
              {t('onboarding.sectorStepTitle')}
            </h1>
          </div>
        </div>
      </motion.header>

      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        className="card flex-1 space-y-5 overflow-y-auto p-5 sm:p-6"
      >
        <div>
          <p className="font-mono text-[11px] font-700 uppercase tracking-[0.18em] text-pink">
            {t('onboarding.sectorPickKicker')}
          </p>
          <h2 className="mt-1.5 font-display text-2xl font-800 tracking-[-0.02em] text-ink">
            {t('onboarding.sectorPickTitle')}
          </h2>
          <p className="mt-2 text-[13.5px] leading-[1.55] text-ink/65">
            {t('onboarding.sectorPickBody')}
          </p>
        </div>

        <SectorPicker compact onSaved={onContinue} />

        <div className="border-t border-ink/5 pt-4 text-center">
          <button
            onClick={onContinue}
            className="text-[12.5px] font-700 text-ink/55 underline-offset-4 hover:underline"
          >
            {t('onboarding.sectorSkip')}
            <ArrowRight size={12} className="ml-1 inline" />
          </button>
        </div>
      </motion.div>
    </div>
  );
}
